import * as React from 'react';
import { bookingShipperRoute, bookingFFRoute } from 'src/routing/utils';
import Tooltip from 'antd/lib/tooltip';
import { InquiryModes, InquiryType } from 'src/Library/Constants';
import { renderInquiryOriginDetails, renderInquiryDestinationDetails } from '../Cards/InquiryCommon';
import { tokens } from 'src/design-system';

const roadModes = [InquiryModes.DOMESTIC, InquiryModes.RPTL];
const trailerAndRoadModes = [InquiryModes.TRAILER, ...roadModes];

export const statusShipper = [{
  data_key: 'all',
  label: 'All',
  color: '#16578B',
  status: 'ALL',
}, {
  data_key: 'open',
  label: 'Booking Requested',
  color: '#51E2E8',
  status: 'OPEN',
}, {
  data_key: 'confirmed',
  label: 'Booked',
  color: '#52C41A',
  status: 'CONFIRMED',
}, {
  data_key: 'declined',
  label: 'Declined',
  color: '#F5222D',
  status: 'DECLINED',
}, {
  data_key: 'cancelled',
  label: 'Cancelled',
  color: '#94171E',
  status: 'CANCELLED',
}];

export const statusFF = (isInttraBookingAllowed = false) => [{
  data_key: 'all',
  label: 'All',
  color: '#444444',
  status: BOOKING_STATUS.ALL,
}, {
  data_key: 'open',
  label: 'New Bookings Received',
  color: '#EABD52',
  status: BOOKING_STATUS.OPEN,
}, ...(isInttraBookingAllowed ? [{
  data_key: 'SLResponseAwaited',
  label: 'SL Response Awaited',
  color: '#ED8732',
  status: BOOKING_STATUS.SL_RESPONSE_AWAITED,
}, {
  data_key: 'SLResponded',
  label: 'SL Responded',
  color: '#4785C8',
  status: BOOKING_STATUS.SL_RESPONDED,
}] : []), {
  data_key: 'confirmed',
  label: 'Booked',
  color: '#6BBA89',
  status: BOOKING_STATUS.CONFIRMED,
}, {
  data_key: 'cancelled',
  label: 'Cancelled by Shipper',
  color: '#999999',
  status: BOOKING_STATUS.CANCELLED,
}, ...(isInttraBookingAllowed ? [{
  data_key: 'SLErrored',
  label: 'Errors Received',
  color: '#E35959',
  status: BOOKING_STATUS.SL_ERRORED,
}] : []), {
  data_key: 'declined',
  label: 'Declined by You',
  color: '#E35959',
  status: BOOKING_STATUS.DECLINED,
}];

export const getStatus = (type : string, isInttraBookingAllowed = false) => {
  return type === 'SHIPPER' ? statusShipper : statusFF(isInttraBookingAllowed);
};

export const carrierListForDOExtraction : string [] = [
  'PARAMOUNT',
  'AVANA',
  'HAPAG',
  'LANCER',
  'TSLINES',
  'EVERGREEN',
  'ONELINE',
  'ZIM',
  'MSC',
  'CMACGM',
  'MAERSK',
  'SAFMARINE'
];

export enum BookingProcess {
  INTTRA = 'INTTRA',
  BOOKING_NOTE = 'BOOKING_NOTE',
};
export enum CargoType {
  GENERAL = 'GENERAL',
  HAZ = 'HAZ',
  REEFER = 'REEFER',
}

export enum SL_RESPONDED_INTTRA_STATUS {
  ALL = 'ALL',
  PARTIAL = 'PARTIAL',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
  CANCELLED = 'CANCELLED',
  INTTRA_REQUEST_SUBMITTED = 'INTTRA_REQUEST_SUBMITTED',
  INTTRA_REQUEST_ACCEPTED = 'INTTRA_REQUEST_ACCEPTED',
}

export enum BOOKING_STATUS {
  ALL = 'ALL',
  OPEN = 'OPEN',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  DECLINED = 'DECLINED',
  SL_RESPONSE_AWAITED = 'SL_RESPONSE_AWAITED',
  SL_RESPONDED = 'SL_RESPONDED',
  SL_ERRORED = 'SL_ERRORED',
}

export const bookingSecondaryActions = {
  SL_RESPONSE_AWAITED: {
    uploadBookingNote: 'Upload Booking Note',
    cancelInttraRequest: 'Cancel INTTRA request',
    declineShipperRequest: 'Decline Shipper request',
  },
  SL_RESPONDED: {
    CONFIRMED: {
      cancelInttraRequest: 'Cancel INTTRA request',
      amendInttraRequest: 'Amend INTTRA request',
      declineShipperRequest: 'Decline Shipper request',
    },
    PARTIAL: {
      cancelInttraRequest: 'Cancel INTTRA request',
      declineShipperRequest: 'Decline Shipper request',
    },
    REJECTED: {
      raiseNewInttraRequest: 'Create New INTTRA request',
      uploadBookingNote: 'Upload Booking Note',
    },
    PENDING: {
      cancelInttraRequest: 'Cancel INTTRA request',
      declineShipperRequest: 'Decline Shipper request',
    },
  },
  SL_ERRORED: {
    uploadBookingNote: 'Upload Booking Note',
    declineShipperRequest: 'Decline Shipper request',
  },
}

export const bookingStepsMap = (isInttraBookingAllowed) : Record<string, number> => {
  if (isInttraBookingAllowed) {
    return ({
      SHIPPER_BOOKING_DETAILS: 0,
      UPDATED_COST_STRUCTURE: 1,
      PROCESS_BOOKING: 2,
      CONFIRMATION: 3,
    })
  }
  return ({
    SHIPPER_BOOKING_DETAILS: 0,
    UPDATED_COST_STRUCTURE: 1,
    PROCESS_BOOKING: 2,
  })
}

export const slRespondedFilters = [
  {
    key: SL_RESPONDED_INTTRA_STATUS.ALL,
    label: 'All',
    status: 'SLResponded',
  },
  {
    key: SL_RESPONDED_INTTRA_STATUS.CONFIRMED,
    label: 'Confirmed',
    status: 'SLRespondedConfirmed',
  },
  {
    key: SL_RESPONDED_INTTRA_STATUS.PARTIAL,
    label: 'Replaced',
    status: 'SLRespondedPartial',
  },
  {
    key: SL_RESPONDED_INTTRA_STATUS.REJECTED,
    label: 'Rejected',
    status: 'SLRespondedRejected',
  },
  {
    key: SL_RESPONDED_INTTRA_STATUS.PENDING,
    label: 'Pending',
    status: 'SLRespondedPending',
  },
];

export const getBookingRoute = (type) => {
  if (type === 'SHIPPER') {
    return bookingShipperRoute;
  }
  return bookingFFRoute;
}

export const renderBookingOriginDetails = (data, classes) => {
  if (trailerAndRoadModes.includes(data.inquiryMode)) {
    return renderInquiryOriginDetails(classes, data);
  };
  const originCountry = data.origin?.countryName;
  return (
    <React.Fragment>
      <Tooltip title={originCountry}>
        <img src={data.origin?.flagS3Link}
          style={{ width: '27px', height: '19px' }} /></Tooltip><br />
      <span style={{ color: '#262626', fontSize: `${tokens.fontSize.body}px`, fontWeight: 600 }}>
        <Tooltip
          title={`${data.origin?.name} (${data.origin?.code})`}
          placement="topLeft"
        >{data.origin?.name}</Tooltip>
      </span>
    </React.Fragment>
  );
};

export const renderBookingDestinationDetails = (data, classes) => {
  if (trailerAndRoadModes.includes(data.inquiryMode)) {
    return renderInquiryDestinationDetails(classes, data);
  };

  const destinationCountry = data.destination?.countryName;
  return (
    <React.Fragment>
      <Tooltip title={destinationCountry}>
        <img src={data.destination?.flagS3Link}
          style={{ width: '27px', height: '19px' }} /></Tooltip><br />
      <div className={classes.absoluteData}
        style={{
          width: '124px', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
        <span style={{ color: '#262626', fontSize: `${tokens.fontSize.body}px`, fontWeight: 600 }}>
          <Tooltip
            title={`${data.destination?.name} (${data.destination?.code})`}
            placement="topLeft"
          >{data.destination?.name}</Tooltip>
        </span>
      </div>
    </React.Fragment>
  );
};

export const commentsLocationDetails = (cardData, location) => {
  let trailerLabel;
  let country;
  let nonTrailerLabel;
  let flagS3Link;
  let pincode;

  const isDomesticMode = roadModes.includes(cardData.inquiryMode);
  
  if (location === 'ORIGIN') {
    trailerLabel = cardData.originLocationDetails?.city || cardData.originLocationDetails?.name;
    country = isDomesticMode ? cardData.originLocationDetails?.countryName : cardData.origin?.countryName;
    nonTrailerLabel = isDomesticMode ? cardData.originLocationDetails?.city : cardData.origin?.name;
    flagS3Link = isDomesticMode ? cardData.originLocationDetails?.flagS3Link : cardData.origin?.flagS3Link;
    pincode = isDomesticMode ? cardData?.originLocationDetails?.pincode : ''
  } else {
    const destination = cardData.inquiryType === InquiryType.EXPORT ?
      (cardData.destinationPortDetails || cardData.destination) :  cardData.destinationLocationDetails;
    trailerLabel = cardData.inquiryType === InquiryType.EXPORT ? destination?.name :
      (destination?.city || destination?.name);
    country = isDomesticMode ? cardData.destinationLocationDetails?.countryName : cardData.destination?.countryName;
    flagS3Link = isDomesticMode ? cardData.destinationLocationDetails?.flagS3Link : cardData.destination?.flagS3Link;
    nonTrailerLabel = isDomesticMode ? cardData.destinationLocationDetails?.city : cardData.destination?.name;
    pincode = isDomesticMode ? cardData?.destinationLocationDetails?.pincode : ''
  }

  if (cardData.inquiryMode === InquiryModes.TRAILER) {
    return (
      <React.Fragment>
        <Tooltip title={trailerLabel}>
          <span style={{ color: '#262626', fontSize: `${tokens.fontSize.body}px`, fontWeight: tokens.fontWeight.semibold,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '110px' }}>
            {trailerLabel}
          </span>
        </Tooltip>
      </React.Fragment>
    );
  }

  if (isDomesticMode) {
    return (
      <div>
        <Tooltip title={country} placement='left'>
          <img
            src={flagS3Link}
            style={{
              width: '27px',
              height: '19px',
              marginRight: '10px',
              border: 'solid 1px rgb(128 128 128 / 16%)'
            }}
          />
        </Tooltip>
        <Tooltip title={nonTrailerLabel}>
          <div
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '10ch',
            }}
          >
            <span style={{ color: '#262626', fontSize: `${tokens.fontSize.body}px`, fontWeight: 600 }}>
              {nonTrailerLabel}
            </span>
          </div>
        </Tooltip>
        <div style={{ fontSize: `${tokens.fontSize.caption}px` }}>{pincode}</div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Tooltip title={country} placement='bottom'>
        <img src={flagS3Link}
          style={{
            width: '27px',
            height: '19px',
            marginRight: '10px',
            border: 'solid 1px rgb(128 128 128 / 16%)'
          }}
        />
      </Tooltip><br />
      <Tooltip title={nonTrailerLabel} placement='bottom'>
        <div style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
          width: '110px',
        }}>
          <span style={{ color: '#262626', fontSize: `${tokens.fontSize.body}px`, fontWeight: 600 }}>
            {nonTrailerLabel}
          </span>
        </div>
      </Tooltip>
    </React.Fragment>
  );
}

export const modeWiseDefaultUndisclosedCarrierCode = {
  [InquiryModes.FCL]: 'UNDISCLOSED',
  [InquiryModes.LCL]: 'UNDISCLOSED',
  [InquiryModes.AIRCARGO]: 'UNDISCLOSED_AIR',
};

export const trackingProviders = {
  SHIPPING_LINE: 'Shipping Line',
  HBL_VENDOR: 'HBL Vendor',
};

export enum MODE_SUPPORTING_AUTO_TRACKING {
	FCL = 'FCL',
	LCL = 'LCL',
}
